import { useEffect, useRef } from 'react'

/** Shared live-refresh cadence across the app. */
export const LIVE_POLL_MS = 3500
export const LIVE_POLL_FAST_MS = 2000
export const LIVE_POLL_SLOW_MS = 8000

/**
 * Call `onTick` on an interval while the document is visible, and immediately
 * on focus / visibility restore. Keeps realtime badges and lists feeling fresh
 * without hammering the API in background tabs.
 */
export function useLiveRefresh(onTick: () => void, intervalMs = LIVE_POLL_MS, enabled = true) {
  const tickRef = useRef(onTick)
  tickRef.current = onTick

  useEffect(() => {
    if (!enabled) return
    const run = () => {
      if (document.visibilityState === 'hidden') return
      tickRef.current()
    }
    run()
    const id = window.setInterval(run, intervalMs)
    const onVis = () => {
      if (document.visibilityState === 'visible') run()
    }
    window.addEventListener('focus', run)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', run)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [enabled, intervalMs])
}
